import type { ChatMessage, JsonValue } from "../types";
import { BaseComponent } from "./base";
import { formatMarkdown } from "../utils/markdown";

export class Message extends BaseComponent {
  private message: ChatMessage;
  private showTimestamp: boolean;
  private onApprove?: (toolId: string, toolName: string, args: Record<string, JsonValue>) => void;
  private onReject?: (toolId: string, toolName: string) => void;

  constructor(
    message: ChatMessage,
    showTimestamp = false,
    onApprove?: (toolId: string, toolName: string, args: Record<string, JsonValue>) => void,
    onReject?: (toolId: string, toolName: string) => void,
  ) {
    super();
    this.message = message;
    this.showTimestamp = showTimestamp;
    this.onApprove = onApprove;
    this.onReject = onReject;
    this.element = this.create();
  }

  protected create(): HTMLElement {
    const wrapper = document.createElement("div");
    wrapper.className = `ac-msg-wrap ac-msg-${this.message.role}`;

    if (this.message.approvalRequired && this.message.approvalMeta) {
      wrapper.appendChild(this.buildApprovalCard(this.message.approvalMeta));
    } else {
      const bubble = document.createElement("div");
      bubble.className = `ac-bubble ac-bubble-${this.message.role}`;
      bubble.innerHTML = formatMarkdown(this.message.content);
      wrapper.appendChild(bubble);
    }

    if (this.showTimestamp) {
      const ts = document.createElement("time");
      ts.className = "ac-timestamp";
      ts.dateTime = new Date(this.message.timestamp).toISOString();
      ts.textContent = this.formatTime(this.message.timestamp);
      wrapper.appendChild(ts);
    }

    return wrapper;
  }

  private buildApprovalCard(meta: NonNullable<ChatMessage["approvalMeta"]>): HTMLElement {
    const card = document.createElement("div");
    card.className = "ac-approval-card";

    const icon = document.createElement("div");
    icon.className = "ac-approval-icon";
    icon.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`;

    const body = document.createElement("div");
    body.className = "ac-approval-body";

    const title = document.createElement("strong");
    title.className = "ac-approval-title";
    title.textContent = `Action required: ${meta.toolName}`;

    const desc = document.createElement("p");
    desc.className = "ac-approval-desc";
    desc.textContent = "The assistant wants to execute this tool. Review the parameters and approve or reject.";

    const pre = document.createElement("pre");
    pre.className = "ac-approval-args";
    pre.textContent = JSON.stringify(meta.arguments, null, 2);

    const actions = document.createElement("div");
    actions.className = "ac-approval-actions";

    const approveBtn = document.createElement("button");
    approveBtn.className = "ac-approval-btn ac-approval-approve";
    approveBtn.textContent = "Approve";
    approveBtn.addEventListener("click", () => {
      this.onApprove?.(meta.toolId, meta.toolName, meta.arguments);
      this.setResolved("approved");
    });

    const rejectBtn = document.createElement("button");
    rejectBtn.className = "ac-approval-btn ac-approval-reject";
    rejectBtn.textContent = "Reject";
    rejectBtn.addEventListener("click", () => {
      this.onReject?.(meta.toolId, meta.toolName);
      this.setResolved("rejected");
    });

    actions.appendChild(approveBtn);
    actions.appendChild(rejectBtn);
    body.appendChild(title);
    body.appendChild(desc);
    body.appendChild(pre);
    body.appendChild(actions);
    card.appendChild(icon);
    card.appendChild(body);

    return card;
  }

  private setResolved(state: "approved" | "rejected"): void {
    const actions = this.element.querySelector(".ac-approval-actions");
    if (!actions) return;
    actions.innerHTML = `<span class="ac-approval-resolved ac-approval-${state}">${state === "approved" ? "✓ Approved" : "✗ Rejected"}</span>`;
  }

  private formatTime(ts: number): string {
    return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
}
